import { createApi } from '@reduxjs/toolkit/query/react';

import type { PropertyPhotoRow, PropertyVideoRow, PropertyYoutubeLinkRow } from '@/types/property';

import { apiBaseUrl } from '../Api';
import { makeBaseQuery } from '../baseQuery';
import type {
  CertificateDownloadArg,
  LawyerCertificateListResult,
  LawyerCertificatesArg,
  LawyerChangePasswordArg,
  LawyerDashboardOverdueRow,
  LawyerDashboardPerformance,
  LawyerDashboardRecentCertificate,
  LawyerDashboardStats,
  LawyerDocumentReviewArg,
  LawyerMessageArg,
  LawyerMonthlyActivity,
  LawyerNoteArg,
  LawyerNotificationIdArg,
  LawyerPropertiesArg,
  LawyerPropertyArg,
  LawyerPropertyDetail,
  LawyerPropertyDocuments,
  LawyerPropertyListResult,
  LawyerProfileResult,
  LawyerRecentAssignment,
  LawyerRow,
  LegalOpinionArg,
  MutationAck
} from './lawyerTypes';

/**
 * `/api/lawyer/*` minus the ownership chain, which is its own router on the backend and its
 * own slice here (`ownershipApi`). Under `/api`, so `apiBaseUrl`.
 *
 * Normalizers are module-scope, take `unknown` and are TOTAL — never throw, never return
 * `undefined`. A throwing normalizer takes out the route's error boundary instead of letting
 * the query surface its own error state.
 */
const asRecord = (value: unknown): Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

const envelope = (response: unknown): unknown => {
  const root = asRecord(response);
  return 'data' in root ? root.data : response;
};

const asArray = <T>(value: unknown): T[] => (Array.isArray(value) ? (value as T[]) : []);

const asString = (value: unknown, fallback = ''): string =>
  typeof value === 'string' ? value : fallback;

const asNumber = (value: unknown, fallback = 0): number => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) {
    return Number(value);
  }
  return fallback;
};

const asRow = (value: unknown): LawyerRow | null => {
  const raw = asRecord(value);
  return Object.keys(raw).length > 0 ? raw : null;
};

/** Axios dropped nullish params; `URLSearchParams` would send the string "undefined". */
const toQuery = (params: Record<string, string | number | undefined | null>): string => {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;
    search.append(key, String(value));
  }
  const qs = search.toString();
  return qs ? `?${qs}` : '';
};

/** A list endpoint answers `[]`, never `undefined` — `rows.map()` must be safe on first render. */
function normalizeRows(response: unknown): LawyerRow[] {
  const data = envelope(response);
  if (Array.isArray(data)) return data as LawyerRow[];
  return asArray<LawyerRow>(asRecord(data).items);
}

export function normalizeLawyerStats(response: unknown): LawyerDashboardStats {
  const data = asRecord(envelope(response));
  return {
    totalAssignedProperties: asNumber(data.totalAssignedProperties),
    pendingReview: asNumber(data.pendingReview),
    reviewsCompleted: asNumber(data.reviewsCompleted),
    documentsFlagged: asNumber(data.documentsFlagged),
    certificatesIssued: asNumber(data.certificatesIssued)
  };
}

export function normalizeRecentAssignments(response: unknown): LawyerRecentAssignment[] {
  return normalizeRows(response) as unknown as LawyerRecentAssignment[];
}

export function normalizeOverdue(response: unknown): LawyerDashboardOverdueRow[] {
  return normalizeRows(response) as unknown as LawyerDashboardOverdueRow[];
}

export function normalizeRecentCertificates(response: unknown): LawyerDashboardRecentCertificate[] {
  return normalizeRows(response) as unknown as LawyerDashboardRecentCertificate[];
}

export function normalizePerformance(response: unknown): LawyerDashboardPerformance {
  const data = asRecord(envelope(response));
  return {
    reviewsThisWeek: asNumber(data.reviewsThisWeek),
    avgReviewDays: asNumber(data.avgReviewDays),
    approvalRate: asNumber(data.approvalRate),
    flagRate: asNumber(data.flagRate),
    monthlyActivity: asArray<LawyerMonthlyActivity>(data.monthlyActivity)
  };
}

/**
 * `{ items, total, page, limit }` — this endpoint has always been flat, so the `pagination`
 * branch exists only to absorb a future change without a component ever noticing.
 */
function normalizeFlatList(response: unknown, fallbackLimit: number): LawyerPropertyListResult {
  const data = envelope(response);
  const items = Array.isArray(data)
    ? (data as LawyerRow[])
    : asArray<LawyerRow>(asRecord(data).items);
  const raw = asRecord(data);
  const nested = asRecord(raw.pagination);
  const source = Object.keys(nested).length > 0 ? nested : raw;
  return {
    items,
    total: asNumber(source.total, items.length),
    page: asNumber(source.page, 1),
    limit: asNumber(source.limit, fallbackLimit)
  };
}

export function normalizeLawyerProperties(response: unknown): LawyerPropertyListResult {
  return normalizeFlatList(response, 10);
}

export function normalizeLawyerCertificates(response: unknown): LawyerCertificateListResult {
  return normalizeFlatList(response, 50);
}

export function normalizeLawyerProperty(response: unknown): LawyerPropertyDetail {
  const data = asRecord(envelope(response));
  return {
    property: asRow(data.property),
    seller: asRow(data.seller),
    owners: asArray<LawyerRow>(data.owners),
    photos: asArray<PropertyPhotoRow>(data.photos),
    videos: asArray<PropertyVideoRow>(data.videos),
    youtubeLinks: asArray<PropertyYoutubeLinkRow>(data.youtubeLinks)
  };
}

export function normalizeLawyerDocuments(response: unknown): LawyerPropertyDocuments {
  const data = asRecord(envelope(response));
  const summaryRaw = asRecord(data.summary);
  const summary: Record<string, number> = {};
  for (const [key, value] of Object.entries(summaryRaw)) summary[key] = asNumber(value);
  return { documents: asArray<LawyerRow>(data.documents), summary };
}

export function normalizeLawyerProfile(response: unknown): LawyerProfileResult {
  const data = asRecord(envelope(response));
  return {
    user: asRecord(data.user),
    profile: asRow(data.profile),
    verifiedByName: typeof data.verifiedByName === 'string' ? data.verifiedByName : null
  };
}

/** The legal opinion is absent until the lawyer files one — `null`, not a hollow object. */
export function normalizeLegalOpinion(response: unknown): LawyerRow | null {
  return asRow(envelope(response));
}

export function normalizeRowList(response: unknown): LawyerRow[] {
  return normalizeRows(response);
}

/** Reads the ROOT `message`, not `data.message` — the write endpoints answer no payload. */
export function normalizeAck(response: unknown): MutationAck {
  const root = asRecord(response);
  return { success: root.success !== false, message: asString(root.message) };
}

export const lawyerApi = createApi({
  reducerPath: 'lawyerApi',
  baseQuery: makeBaseQuery(apiBaseUrl),
  tagTypes: [
    'DashboardStats',
    'Property',
    'PropertyDocument',
    'LegalOpinion',
    'Certificate',
    'LawyerNote',
    'LawyerMessage',
    'Profile',
    'Notification'
  ],
  endpoints: (builder) => ({
    getLawyerDashboardStats: builder.query<LawyerDashboardStats, void>({
      query: () => ({ url: 'lawyer/dashboard/stats' }),
      transformResponse: (response: unknown) => normalizeLawyerStats(response),
      providesTags: ['DashboardStats']
    }),
    getLawyerRecentAssignments: builder.query<LawyerRecentAssignment[], void>({
      query: () => ({ url: 'lawyer/dashboard/recent-assignments' }),
      transformResponse: (response: unknown) => normalizeRecentAssignments(response),
      providesTags: [{ type: 'Property', id: 'RECENT_ASSIGNMENTS' }]
    }),
    getLawyerDashboardOverdue: builder.query<LawyerDashboardOverdueRow[], void>({
      query: () => ({ url: 'lawyer/dashboard/overdue' }),
      transformResponse: (response: unknown) => normalizeOverdue(response),
      providesTags: [{ type: 'Property', id: 'OVERDUE' }]
    }),
    getLawyerDashboardRecentCertificates: builder.query<LawyerDashboardRecentCertificate[], void>({
      query: () => ({ url: 'lawyer/dashboard/recent-certificates' }),
      transformResponse: (response: unknown) => normalizeRecentCertificates(response),
      providesTags: [{ type: 'Certificate', id: 'RECENT' }]
    }),
    getLawyerDashboardPerformance: builder.query<LawyerDashboardPerformance, void>({
      query: () => ({ url: 'lawyer/dashboard/performance' }),
      transformResponse: (response: unknown) => normalizePerformance(response),
      providesTags: [{ type: 'DashboardStats', id: 'PERFORMANCE' }]
    }),
    getLawyerProperties: builder.query<LawyerPropertyListResult, LawyerPropertiesArg | void>({
      query: (arg) => ({ url: `lawyer/properties${toQuery({ ...(arg ?? {}) })}` }),
      transformResponse: (response: unknown) => normalizeLawyerProperties(response),
      providesTags: (result) => [
        { type: 'Property' as const, id: 'LIST' },
        ...(result?.items ?? []).map((row) => ({
          type: 'Property' as const,
          id: String(row.id)
        }))
      ]
    }),
    getLawyerProperty: builder.query<LawyerPropertyDetail, LawyerPropertyArg>({
      query: ({ propertyId }) => ({ url: `lawyer/properties/${propertyId}` }),
      transformResponse: (response: unknown) => normalizeLawyerProperty(response),
      providesTags: (_result, _error, { propertyId }) => [
        { type: 'Property', id: String(propertyId) }
      ]
    }),
    getLawyerPropertyDocuments: builder.query<LawyerPropertyDocuments, LawyerPropertyArg>({
      query: ({ propertyId }) => ({ url: `lawyer/properties/${propertyId}/documents` }),
      transformResponse: (response: unknown) => normalizeLawyerDocuments(response),
      providesTags: (_result, _error, { propertyId }) => [
        { type: 'PropertyDocument', id: String(propertyId) }
      ]
    }),
    /**
     * `propertyId` rides in the arg for tag targeting only — the URL keys off `docId` and the
     * body carries exactly the four review fields the axios service sent, no more.
     */
    reviewLawyerDocument: builder.mutation<MutationAck, LawyerDocumentReviewArg>({
      query: ({ docId, action, issueType, comments, riskLevel }) => ({
        url: `lawyer/documents/${docId}/review`,
        method: 'PUT',
        body: { action, issueType, comments, riskLevel }
      }),
      transformResponse: (response: unknown) => normalizeAck(response),
      invalidatesTags: (_result, _error, { propertyId }) => [
        { type: 'PropertyDocument', id: String(propertyId) },
        'DashboardStats'
      ]
    }),
    getLegalOpinion: builder.query<LawyerRow | null, LawyerPropertyArg>({
      query: ({ propertyId }) => ({ url: `lawyer/properties/${propertyId}/legal-opinion` }),
      transformResponse: (response: unknown) => normalizeLegalOpinion(response),
      providesTags: (_result, _error, { propertyId }) => [
        { type: 'LegalOpinion', id: String(propertyId) }
      ]
    }),
    /** Filing an opinion issues the certificate too, so the certificate lists and the
     * dashboard counters go stale in the same write. */
    submitLegalOpinion: builder.mutation<MutationAck, LegalOpinionArg>({
      query: ({ propertyId, body }) => ({
        url: `lawyer/properties/${propertyId}/legal-opinion`,
        method: 'POST',
        body
      }),
      transformResponse: (response: unknown) => normalizeAck(response),
      invalidatesTags: (_result, _error, { propertyId }) => [
        { type: 'LegalOpinion', id: String(propertyId) },
        { type: 'Property', id: String(propertyId) },
        { type: 'Certificate', id: 'LIST' },
        { type: 'Certificate', id: 'RECENT' },
        'DashboardStats'
      ]
    }),
    getLawyerCertificates: builder.query<LawyerCertificateListResult, LawyerCertificatesArg | void>(
      {
        query: (arg) => ({ url: `lawyer/certificates${toQuery({ ...(arg ?? {}) })}` }),
        transformResponse: (response: unknown) => normalizeLawyerCertificates(response),
        providesTags: (result) => [
          { type: 'Certificate' as const, id: 'LIST' },
          ...(result?.items ?? []).map((row) => ({
            type: 'Certificate' as const,
            id: String(row.id)
          }))
        ]
      }
    ),
    /**
     * A PDF, not an envelope: `responseHandler` returns the Blob untouched, and there is no
     * `transformResponse` to strip. `keepUnusedDataFor: 0` so a multi-MB Blob is not parked
     * in the store after the download link fires.
     */
    downloadCertificate: builder.query<Blob, CertificateDownloadArg>({
      query: ({ opinionId }) => ({
        url: `lawyer/certificates/${opinionId}/download`,
        responseHandler: (response) => response.blob()
      }),
      keepUnusedDataFor: 0
    }),
    getLawyerNotes: builder.query<LawyerRow[], LawyerPropertyArg>({
      query: ({ propertyId }) => ({ url: `lawyer/properties/${propertyId}/notes` }),
      transformResponse: (response: unknown) => normalizeRowList(response),
      providesTags: (_result, _error, { propertyId }) => [
        { type: 'LawyerNote', id: String(propertyId) }
      ]
    }),
    addLawyerNote: builder.mutation<MutationAck, LawyerNoteArg>({
      query: ({ propertyId, note }) => ({
        url: `lawyer/properties/${propertyId}/notes`,
        method: 'POST',
        body: { note }
      }),
      transformResponse: (response: unknown) => normalizeAck(response),
      invalidatesTags: (_result, _error, { propertyId }) => [
        { type: 'LawyerNote', id: String(propertyId) }
      ]
    }),
    getLawyerMessages: builder.query<LawyerRow[], LawyerPropertyArg>({
      query: ({ propertyId }) => ({ url: `lawyer/properties/${propertyId}/messages` }),
      transformResponse: (response: unknown) => normalizeRowList(response),
      providesTags: (_result, _error, { propertyId }) => [
        { type: 'LawyerMessage', id: String(propertyId) }
      ]
    }),
    sendLawyerMessage: builder.mutation<MutationAck, LawyerMessageArg>({
      query: ({ propertyId, message }) => ({
        url: `lawyer/properties/${propertyId}/messages`,
        method: 'POST',
        body: { message }
      }),
      transformResponse: (response: unknown) => normalizeAck(response),
      invalidatesTags: (_result, _error, { propertyId }) => [
        { type: 'LawyerMessage', id: String(propertyId) }
      ]
    }),
    getLawyerProfile: builder.query<LawyerProfileResult, void>({
      query: () => ({ url: 'lawyer/profile' }),
      transformResponse: (response: unknown) => normalizeLawyerProfile(response),
      providesTags: ['Profile']
    }),
    updateLawyerProfile: builder.mutation<MutationAck, Record<string, unknown>>({
      query: (body) => ({ url: 'lawyer/profile', method: 'PUT', body }),
      transformResponse: (response: unknown) => normalizeAck(response),
      invalidatesTags: ['Profile']
    }),
    changeLawyerPassword: builder.mutation<MutationAck, LawyerChangePasswordArg>({
      query: (body) => ({ url: 'lawyer/profile/change-password', method: 'PUT', body }),
      transformResponse: (response: unknown) => normalizeAck(response)
    }),
    getLawyerNotifications: builder.query<LawyerRow[], void>({
      query: () => ({ url: 'lawyer/notifications' }),
      transformResponse: (response: unknown) => normalizeRowList(response),
      providesTags: [{ type: 'Notification', id: 'LIST' }]
    }),
    markLawyerNotificationRead: builder.mutation<MutationAck, LawyerNotificationIdArg>({
      query: ({ id }) => ({ url: `lawyer/notifications/${id}/read`, method: 'PUT' }),
      transformResponse: (response: unknown) => normalizeAck(response),
      invalidatesTags: [{ type: 'Notification', id: 'LIST' }]
    }),
    markAllLawyerNotificationsRead: builder.mutation<MutationAck, void>({
      query: () => ({ url: 'lawyer/notifications/read-all', method: 'PUT' }),
      transformResponse: (response: unknown) => normalizeAck(response),
      invalidatesTags: [{ type: 'Notification', id: 'LIST' }]
    })
  })
});

export const {
  useGetLawyerDashboardStatsQuery,
  useGetLawyerRecentAssignmentsQuery,
  useGetLawyerDashboardOverdueQuery,
  useGetLawyerDashboardRecentCertificatesQuery,
  useGetLawyerDashboardPerformanceQuery,
  useGetLawyerPropertiesQuery,
  useGetLawyerPropertyQuery,
  useGetLawyerPropertyDocumentsQuery,
  useReviewLawyerDocumentMutation,
  useGetLegalOpinionQuery,
  useSubmitLegalOpinionMutation,
  useGetLawyerCertificatesQuery,
  useLazyDownloadCertificateQuery,
  useGetLawyerNotesQuery,
  useAddLawyerNoteMutation,
  useGetLawyerMessagesQuery,
  useSendLawyerMessageMutation,
  useGetLawyerProfileQuery,
  useUpdateLawyerProfileMutation,
  useChangeLawyerPasswordMutation,
  useGetLawyerNotificationsQuery,
  useMarkLawyerNotificationReadMutation,
  useMarkAllLawyerNotificationsReadMutation
} = lawyerApi;
